import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, forwardRef, Inject, Input, Optional } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { DYNAMIC_COMPONENT_CONFIG } from '@eui/core';
import { EuiTimePicker } from './models/eui-timepicker.model';

export const EUI_TIMEPICKER_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => EuiTimepickerComponent),
    multi: true,
};
@Component({
    selector: 'eui-timepicker',
    templateUrl: './eui-timepicker.component.html',
    styleUrls: ['./styles/_index.scss'],
    providers: [EUI_TIMEPICKER_ACCESSOR],
})
export class EuiTimepickerComponent implements ControlValueAccessor {

    hours: number;
    mins: number;
    secs: number;
    isDatetimepicker: boolean;

    propagatedValues: EuiTimePicker = {
        hours: null,
        mins: null,
        secs: this.hasSeconds ? null : undefined
    };

    @Input()
    get isreadOnly() { return this._isreadOnly; }
    set isreadOnly(value) { this._isreadOnly = coerceBooleanProperty(value); }
    _isreadOnly = false;

    @Input()
    get hasSeconds() { return this._hasSeconds; }
    set hasSeconds(value) { this._hasSeconds = coerceBooleanProperty(value); }
    _hasSeconds = false;

    // tslint:disable-next-line: max-line-length
    constructor(@Optional() @Inject(DYNAMIC_COMPONENT_CONFIG) config: {hours: number, mins: number, secs: number, callbackFn: any, isDatetimepicker: boolean}) {  // TO BE UPDATED TO EuiDateTimePickerConfig
        this.hours = config?.hours || 0;
        this.mins = config?.mins || 0;
        this.secs = config?.secs || 0; // Seconds to be added on EuiDateTimePickerConfig
        this.isDatetimepicker = config?.isDatetimepicker;
        this.callbackFn = config?.callbackFn || this.callbackFn;
    }

    callbackFn: (hours: number, mins: number, secs?: number) => void = () => {
    }

    changeHour(step: number) {
        const val = (isNaN(this.hours) ? 0 : this.hours) + (step);
        this.updateHour(val.toString());
    }

    changeMinutes(step: number) {
        const val = (isNaN(this.mins) ? 0 : this.mins) + (step);
        this.updateMinutes(val.toString());
    }

    changeSeconds(step: number) {
        const val = (isNaN(this.secs) ? 0 : this.secs) + (step);
        this.updateSeconds(val.toString());
    }

    updateHour(newVal: string) {
        const enteredHour = this.toInteger(newVal);

        if (!isNaN(enteredHour)) {
            this.hours = (enteredHour < 0 ? 24 + enteredHour : enteredHour) % 24;
        } else {
            this.hours = NaN;
        }

        this.callbackFn(this.hours, this.mins);

        this.propagatedValues.hours = this.padNumber(this.hours) ;
        this.propagateChange(this.propagatedValues);
    }

    updateMinutes(newVal: string) {
        const enteredMin = this.toInteger(newVal);

        if (!isNaN(enteredMin)) {
            this.mins = enteredMin % 60 < 0 ? 60 + enteredMin % 60 : enteredMin % 60;
            this.changeHour(Math.floor(enteredMin / 60));
        } else {
            this.mins = NaN;
        }

        this.callbackFn(this.hours, this.mins);

        this.propagatedValues.mins = this.padNumber(this.mins);
        this.propagateChange(this.propagatedValues);
    }

    updateSeconds(newVal: string) {
        const enteredSec = this.toInteger(newVal);

        if (!isNaN(enteredSec)) {
            this.secs = enteredSec % 60 < 0 ? 60 + enteredSec % 60 : enteredSec % 60;
            this.changeMinutes(Math.floor(enteredSec / 60));
        } else {
            this.secs = NaN;
        }

        this.callbackFn(this.hours, this.mins, this.secs);

        this.propagatedValues.secs = this.padNumber(this.secs);
        this.propagateChange(this.propagatedValues);
    }

    formatHour(value: number) {
        if (this.isNumber(value)) {
            return this.padNumber(value % 24);
        } else {
            return this.padNumber(NaN);
        }
    }

    formatMins(value: number) {
        return this.padNumber(value);
    }

    formatSecs(value: number) {
        return this.padNumber(value);
    }

    toInteger(value: any): number {
        return parseInt(`${value}`, 10);
    }

    isNumber(value: any): value is number {
        return !isNaN(this.toInteger(value));
    }

    padNumber(value: number) {
        if (this.isNumber(value)) {
            return `0${value}`.slice(-2);
        } else {
            return '00';
        }
    }

    restrictHoursLength(inputref: HTMLInputElement) {
        let inputHoursValue = inputref.value;
        if (inputHoursValue.length === 2) {
            return false;
        }
    }

    restrictMinsLength(inputref: HTMLInputElement) {
        let inputMinsValue = inputref.value;
        if (inputMinsValue.length === 2) {
            return false;
        }
    }

    restrictSecsLength(inputref: HTMLInputElement) {
        let inputSecsValue = inputref.value;
        if (inputSecsValue.length === 2) {
            return false;
        }
    }

    writeValue(values: EuiTimePicker): void {
        if (typeof values !== 'object' || Array.isArray(values)) { throw new Error('value must be an EuiTimePicker Object'); }
        if (!values.hours || typeof values.hours !== 'string' ) {
            throw new Error('hours must be defined and be a string with range 00-23'); }
        if (!values.mins || typeof values.mins !== 'string' ) {
            throw new Error('mins must be defined and be a string with range 00-59'); }
        if (this.hasSeconds && (!values.secs || typeof values.secs !== 'string' )) {
            throw new Error('secs must be defined and be a string with range 00-59'); }

        this.updateHour(values.hours);
        this.updateMinutes(values.mins);
        if (values.secs) {
            this.updateSeconds(values.secs);
        }
    }

    registerOnChange(fn: any): void {
        this.propagateChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.propagateTouched = fn;
    }

    private propagateChange = (_: any) => {
    }

    private propagateTouched = () => {
    }
}
